import { useEffect } from "react";
import { usePersistedState } from "@/lib/lib-use-persisted-state";

/**
 * Escape hatches that bring the username/password form back when the
 * `hidePasswordLogin` runtime flag has hidden it (see `config.ts`).
 *
 * Two of them, because they cover different situations:
 *
 *  - typing `froy` on the login page — for a person standing in front of the
 *    browser. A *sequence* rather than a chord on purpose: nearly every
 *    `Ctrl+Shift+<letter>` is already claimed by Chrome, Firefox or a password
 *    manager, and `Ctrl+Alt` is AltGr on the Norwegian layout. A sequence
 *    collides with nothing, and keystrokes typed into a text field are ignored,
 *    so an email like `dekk.froy-hovding@…` can't trip it.
 *  - `?login=local` — dictatable over the phone, bookmarkable, and usable from
 *    a script or a Playwright run that cannot send synthetic keystrokes.
 *
 * Once revealed, the password form *replaces* the Microsoft button (typing
 * `froy` again swaps back), and the choice is remembered in localStorage so a
 * shared bridge PC keeps showing the password form after a sign-out.
 *
 * Neither is a security boundary. PocketBase still accepts password auth
 * either way; hiding the form only steers real users towards SSO.
 */

export const REVEAL_SEQUENCE = "froy";

/** A gap longer than this starts the sequence over. */
export const REVEAL_SEQUENCE_TIMEOUT_MS = 2_000;

export const REVEAL_SEARCH_PARAM = "login";
export const REVEAL_SEARCH_VALUE = "local";

export type SequenceMatcher = {
  /** Feed one keystroke. Returns true on the keystroke that completes the sequence. */
  push: (key: string, now: number) => boolean;
};

/**
 * Matches `sequence` over a stream of keystrokes, restarting on a wrong key or
 * a pause longer than `timeoutMs`.
 *
 * A mismatched key is not simply dropped: it is re-tried as the first key of a
 * fresh attempt, so a fumbled `ffroy` still matches rather than needing the
 * typist to notice and pause.
 */
export function createSequenceMatcher(
  sequence = REVEAL_SEQUENCE,
  timeoutMs = REVEAL_SEQUENCE_TIMEOUT_MS,
): SequenceMatcher {
  const expected = [...sequence.toLowerCase()];
  let index = 0;
  let lastAt = Number.NEGATIVE_INFINITY;

  return {
    push(key, now) {
      const char = key.toLowerCase();
      if (now - lastAt > timeoutMs) index = 0;
      lastAt = now;

      if (char !== expected[index]) {
        // Wrong key — restart, but let this keystroke open the new attempt.
        index = char === expected[0] ? 1 : 0;
        return false;
      }

      index += 1;
      if (index < expected.length) return false;
      index = 0;
      return true;
    },
  };
}

/** localStorage key (inside `usePersistedState`'s store) for the remembered choice. */
export const REVEAL_STORAGE_KEY = "login:passwordFormRevealed";

/** Keystrokes aimed at a text field are the user typing, not the sequence. */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

/**
 * True while the password form should replace the Microsoft button. Typing
 * {@link REVEAL_SEQUENCE} toggles it, `forced` (`?login=local`) switches it
 * on, and either way the choice persists across visits and sign-outs. Listens
 * only while `enabled` — no listener at all in the environments that show
 * both forms anyway.
 */
export function usePasswordFormRevealed(enabled: boolean, forced: boolean): boolean {
  const [revealed, setRevealed] = usePersistedState<boolean>(REVEAL_STORAGE_KEY, false);

  useEffect(() => {
    if (enabled && forced) setRevealed(true);
  }, [enabled, forced, setRevealed]);

  useEffect(() => {
    if (!enabled) return;

    const matcher = createSequenceMatcher();
    const onKeyDown = (event: KeyboardEvent) => {
      // A modifier means the user is reaching for a browser shortcut, and a
      // multi-character `key` is a named key (Tab, Enter, …) — neither is part
      // of the sequence.
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.key.length !== 1) return;
      if (isEditableTarget(event.target)) return;
      if (matcher.push(event.key, Date.now())) setRevealed((prev) => !prev);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled, setRevealed]);

  return enabled && revealed;
}
